import React, { useState, useEffect } from "react";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import Card from "../modern-components/ui/Card";
import Button from "../modern-components/ui/Button";
import Input from "../modern-components/ui/Input";
import Textarea from "../modern-components/ui/Textarea";
import { contactAPI, siteSettingsAPI } from "../services/api";

const DEFAULT_SETTINGS = {
  phone_primary: "+91 94370 45109",
  phone_secondary: "+91 91240 75890",
  email: "info@lohamart.in",
  address: "S3/16, Industrial Estate Kalunga, Rourkela, Odisha 769004",
  business_hours: "Mon - Sat: 9:00 AM - 6:00 PM",
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    siteSettingsAPI
      .getSettings()
      .then((res) => {
        const data = res.data.data;
        setSettings({
          phone_primary: data.phone_primary || DEFAULT_SETTINGS.phone_primary,
          phone_secondary:
            data.phone_secondary || DEFAULT_SETTINGS.phone_secondary,
          email: data.email || DEFAULT_SETTINGS.email,
          address: data.address || DEFAULT_SETTINGS.address,
          business_hours:
            data.business_hours || DEFAULT_SETTINGS.business_hours,
        });
      })
      .catch(() => {
        // Keep defaults if the settings API is unreachable
      });
  }, []);

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      details: [settings.phone_primary, settings.phone_secondary].filter(
        Boolean,
      ),
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      icon: Mail,
      title: "Email",
      details: [settings.email].filter(Boolean),
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      icon: MapPin,
      title: "Address",
      details: [settings.address].filter(Boolean),
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: [settings.business_hours].filter(Boolean),
      color: "text-orange-600",
      bgColor: "bg-orange-100",
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Phone must be 10 digits";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      await contactAPI.submitQuery(formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      setSubmitError(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get in Touch
          </h1>
          <p className="text-lg text-gray-600">
            Have questions? We'd love to hear from you. Send us a message and
            we'll respond as soon as possible.
          </p>
        </div>

        {/* Contact Info Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactInfo.map((info, index) => {
            const Icon = info.icon;
            return (
              <Card
                key={info.title}
                className="text-center animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div
                  className={`w-14 h-14 ${info.bgColor} rounded-xl flex items-center justify-center mx-auto mb-4`}
                >
                  <Icon className={`w-7 h-7 ${info.color}`} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  {info.title}
                </h3>
                {info.details.map((detail, i) => (
                  <p key={i} className="text-sm text-gray-600">
                    {detail}
                  </p>
                ))}
              </Card>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="animate-slide-up">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Send us a Message
            </h2>

            {submitted ? (
              <div className="py-12 text-center animate-scale-up">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Message Sent Successfully!
                </h3>
                <p className="text-gray-600">
                  Thank you for contacting us. We'll get back to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Input
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={errors.name}
                    placeholder="John Doe"
                    required
                  />
                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <Input
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    error={errors.phone}
                    placeholder="+91 9999999999"
                    required
                  />
                  <Input
                    label="Company Name"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Company (Optional)"
                  />
                </div>

                <Textarea
                  label="Message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  error={errors.message}
                  placeholder="Tell us about your requirements..."
                  rows={6}
                  required
                />

                {submitError && (
                  <p className="text-sm text-red-600 text-center">
                    {submitError}
                  </p>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={submitting}
                  rightIcon={<Send className="w-5 h-5" />}
                >
                  {submitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            )}
          </Card>

          {/* Map or Additional Info */}
          <div className="space-y-8 animate-fade-in">
            {/* Map Placeholder */}
            <Card padding="none" className="overflow-hidden h-80">
              <iframe
                title="Loha Mart Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3507.6044442024675!2d77.0266455!3d28.4594965!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDI3JzM0LjIiTiA3N8KwMDEnMzYuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
              />
            </Card>

            {/* FAQ Card */}
            <Card className="bg-gradient-to-br from-primary-50 to-secondary-50">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Need Immediate Assistance?
              </h3>
              <p className="text-gray-600 mb-4">
                For urgent queries, you can reach us directly via phone or
                WhatsApp. Our team is available during business hours to assist
                you.
              </p>
              <a
                href={`tel:${settings.phone_primary.replace(/\s/g, "")}`}
                className="block"
              >
                <Button variant="primary" fullWidth>
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now: {settings.phone_primary}
                </Button>
              </a>
            </Card>

            {/* Business Info */}
            <Card>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Regional Offices
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="font-semibold text-gray-900">West India</div>
                  <div className="text-sm text-gray-600">Ahmedabad</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">East India</div>
                  <div className="text-sm text-gray-600">Odisha</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
